"use client";

import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "en" | "fr" | "ar" | "pl" | "cs";

export const languages: { code: Language; label: string; dir: "ltr" | "rtl" }[] = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "pl", label: "Polski", dir: "ltr" },
  { code: "cs", label: "Čeština", dir: "ltr" },
];

export const translations = {
  en: {
    // Nav
    nav_gallery: "Gallery",
    nav_adventures: "Adventures",
    nav_booking: "Book Now",
    nav_whatsapp: "WhatsApp",
    // Footer
    footer_contact_us: "Contact Us",
    footer_visit_us: "Visit Us",
    footer_location: "Location",
    footer_view_maps: "View on Google Maps",
    footer_shop: "Shop",
    footer_adventures: "Adventures",
    footer_info: "Info",
    footer_legal: "Legal",
    footer_carpets: "Carpets",
    footer_souvenirs: "Souvenirs",
    footer_decor: "Decor",
    footer_quad: "Quad Tours",
    footer_buggy: "Buggy Tours",
    footer_camel: "Camel Rides",
    footer_about: "About Us",
    footer_contact: "Contact",
    footer_faq: "FAQ",
    footer_shipping: "Shipping",
    footer_privacy: "Privacy",
    footer_terms: "Terms",
    footer_rights: "All rights reserved.",
    footer_email_label: "Email",
    footer_phone_label: "Phone",
    // Booking Page
    booking_badge: "Reserve Your Experience",
    booking_title: "Book an Adventure",
    booking_subtitle: "Fill in the form below and we\'ll confirm your booking shortly.",
    booking_success_title: "Request Sent!",
    booking_success_msg: "Your request has been sent. We will contact you shortly.",
    booking_another: "Make Another Booking",
    booking_fullname: "Full Name",
    booking_email: "Email Address",
    booking_phone: "Phone Number",
    booking_persons: "Number of Persons",
    booking_activity: "Selected Activity / Service",
    booking_activity_placeholder: "— Choose an activity —",
    booking_date: "Preferred Date",
    booking_time: "Preferred Time",
    booking_notes: "Additional Notes / Message",
    booking_notes_optional: "(optional)",
    booking_notes_placeholder: "Any special requests, questions, or details about your group...",
    booking_submit: "Send Booking Request",
    booking_sending: "Sending Request...",
    booking_footer_note: "We\'ll get back to you within 24 hours to confirm your booking.",
    booking_land: "🏜️ Land Adventures",
    booking_sea: "🌊 Sea Activities",
    booking_excursions: "🗺️ Excursions",
    err_fullname: "Full name is required.",
    err_email_required: "Email address is required.",
    err_email_invalid: "Please enter a valid email address.",
    err_phone: "Phone number is required.",
    err_activity: "Please select an activity.",
    err_date: "Preferred date is required.",
    err_time: "Preferred time is required.",
    // Adventures Hero
    adv_hero_title: "Explore",
    adv_hero_subtitle: "Djerba",
    adv_hero_desc: "Unforgettable desert adventures await.",
    adv_hero_desc_bold: " Quad tours, buggy rides, camel treks, and horseback experiences",
    adv_hero_desc_end: " led by expert guides.",
    adv_hero_book: "Book Now",
    adv_hero_whatsapp: "WhatsApp",
    adv_hero_guides: "Licensed Guides",
    adv_hero_family: "Family Friendly",
    adv_hero_flexible: "Flexible Times",
    adv_hero_pickup: "Hotel Pickup",
    // Experiences Section
    exp_badge: "Our Adventures",
    exp_title: "Choose Your Experience",
    exp_desc: "From thrilling desert rides to peaceful sea cruises and unforgettable excursions — we have the perfect adventure for every traveller.",
    exp_book_now: "Book Now",
    exp_popular: "Popular",
    exp_best_value: "Best Value",
    exp_unique: "Unique",
    exp_thrilling: "Thrilling",
    exp_scenic: "Scenic",
    exp_family_fun: "Family Fun",
    exp_recommended: "Recommended",
    // Activity names
    act_buggy: "Buggy Tours",
    act_buggy_desc: "Conquer the dunes in a high-performance buggy. An adrenaline-packed ride through stunning desert landscapes.",
    act_horse: "Horse Riding",
    act_horse_desc: "Ride majestic Arabian horses along pristine beaches and scenic trails. Options for all skill levels.",
    act_camel: "Camel Tour",
    act_camel_desc: "Travel like ancient nomads on a peaceful camel trek through the Sahara. Perfect for families and sunset photography.",
    act_quad: "Quad Tours",
    act_quad_desc: "Experience the thrill of riding powerful quad bikes through stunning desert landscapes and Berber villages.",
    act_caleche: "Calèche (Horse Carriage)",
    act_caleche_desc: "Explore local landmarks in a charming traditional horse carriage. Visit the crocodile park, lagoon, and market.",
    act_caleche_horse: "Calèche + Horse Experience",
    act_caleche_horse_desc: "A unique combined experience featuring a horse carriage ride, a meal, and swimming with horses. Unforgettable!",
    act_jetski: "Jet Ski",
    act_jetski_desc: "Feel the rush of the Mediterranean on a powerful jet ski. Solo or shared options available.",
    act_boat: "Boat Ride",
    act_boat_desc: "Enjoy a relaxing boat ride along the beautiful Djerba coastline with stunning sea views.",
    act_parasailing: "Parasailing",
    act_parasailing_desc: "Soar above the Mediterranean and enjoy breathtaking aerial views of Djerba\'s coastline.",
    act_water: "Water Games",
    act_water_desc: "Fun-filled water activities for all ages — banana boats, inflatables, and more. Perfect for groups and families.",
    act_pirate: "Pirate Boat",
    act_pirate_desc: "Ahoy! Embark on a fun pirate-themed boat cruise along the Djerba coast. Great for families and groups.",
    act_island: "Island Tour",
    act_island_desc: "Discover the highlights of beautiful Djerba island on a guided tour covering key landmarks and hidden gems.",
    act_ksar: "Ksar Ghilane Excursion",
    act_ksar_desc: "Journey deep into the Sahara to the legendary Ksar Ghilane oasis. Optional overnight with tent and sunset dinner.",
    act_tataouine: "Tataouine Trip",
    act_tataouine_desc: "Explore the ancient Berber ksour (fortified granaries) and dramatic landscapes of Tataouine — a true journey back in time.",
    // Pricing labels
    price_individual: "Individual (1h30)",
    price_normal: "Normal Ride (1h)",
    price_pro: "Professional Ride (2h30)",
    price_croc: "Crocodile Park",
    price_lagoon: "Lagoon",
    price_market: "Market",
    price_per_person: "Per person",
    price_per_person_min2: "Per person (min. 2)",
    price_standard: "Standard",
    price_tent: "With tent + sunset dinner",
    price_30min: "30 min",
    price_contact: "Contact us",
    note_thu: "Thursday departure: 14:00",
    note_fri: "Friday departure: 08:45",
    note_dep: "Departures: 08:45 & 15:30",
    note_meal: "Includes meal + swimming with horse",
    note_min2: "Minimum 2 persons",
    // Section titles
    sec_land: "Land Adventures",
    sec_sea: "Sea Activities",
    sec_excursions: "Excursions",
    // How to Book
    htb_badge: "Simple Process",
    htb_title: "How to Book Your Adventure",
    htb_desc: "Booking is quick and easy. Follow these simple steps to secure your desert experience.",
    htb_step: "Step",
    htb_ready: "Ready to start?",
    htb_whatsapp: "Book via WhatsApp",
    htb_s1_title: "Select Experience",
    htb_s1_desc: "Choose your preferred adventure from our quad, buggy, camel, or horse riding options.",
    htb_s2_title: "Pick Date & Time",
    htb_s2_desc: "Select your preferred date and time slot. Morning, afternoon, and sunset options available.",
    htb_s3_title: "Confirm Details",
    htb_s3_desc: "Provide number of people, hotel location for pickup, and any special requests.",
    htb_s4_title: "Enjoy Adventure",
    htb_s4_desc: "We\'ll pick you up from your hotel. Get ready for an unforgettable desert experience!",
    // FAQ
    faq_badge: "FAQ",
    faq_title: "Frequently Asked Questions",
    faq_desc: "Everything you need to know before booking your desert adventure. Can\'t find your answer? Contact us via WhatsApp.",
    faq_still: "Still have questions?",
    faq_chat: "Chat with us on WhatsApp",
    faq_q1: "What are the age requirements?",
    faq_a1: "Quad and buggy tours require drivers to be 18+ with a valid license. Passengers can be 12+. Camel and horse rides are suitable for all ages (children under 10 must be accompanied by an adult).",
    faq_q2: "Do I need prior experience?",
    faq_a2: "No experience necessary! All adventures include a safety briefing and basic training. Our professional guides will teach you everything you need to know. We cater to both beginners and experienced riders.",
    faq_q3: "What should I bring?",
    faq_a3: "Comfortable closed-toe shoes, sunglasses, sunscreen, and a camera. We provide all safety equipment (helmets, protective gear). Light clothing and a scarf for dust protection are recommended.",
    faq_q4: "What if the weather is bad?",
    faq_a4: "Safety first! If weather conditions are unsafe, we'll reschedule your adventure or provide a full refund. We monitor weather closely and will contact you in advance if changes are needed.",
    faq_q5: "What\'s your cancellation policy?",
    faq_a5: "Free cancellation up to 24 hours before your adventure. Cancellations within 24 hours are subject to a 50% fee. No-shows are non-refundable. We\'re flexible and will work with you on rescheduling.",
    faq_q6: "Do you provide hotel pickup?",
    faq_a6: "Yes! Hotel pickup is included for all adventures. We cover most hotels in Djerba. Please provide your hotel name when booking, and we'll confirm pickup time (usually 30-60 minutes before start time).",
    faq_q7: "Can I book for a large group?",
    faq_a7: "Absolutely! We specialize in group bookings for families, corporate events, and travel agencies. Groups of 10+ receive special discounts. Contact us via WhatsApp for custom group packages.",
    faq_q8: "What payment methods do you accept?",
    faq_a8: "We accept cash (TND, USD, EUR), credit cards (Visa, Mastercard), and bank transfers. A 30% deposit secures your booking, with the balance due on the day of your adventure.",
    // Online Shop Hero
    shop_hero_title: "Authentic",
    shop_hero_subtitle: "Tunisian Crafts",
    shop_hero_desc: "Discover handmade treasures from the heart of Djerba.",
    shop_hero_desc_bold: " Traditional carpets, unique souvenirs,",
    shop_hero_desc_end: " and unforgettable experiences.",
    shop_hero_explore: "Explore Gallery",
    shop_hero_book: "Book an Experience",
    shop_hero_authentic: "100% Authentic",
    shop_hero_shipping: "Worldwide Shipping",
    shop_hero_customers: "500+ Happy Customers",
    // About Section
    about_badge: "Our Heritage",
    about_title: "Preserving Tunisian Craftsmanship for Generations",
    about_p1: "For over three generations, our family has been weaving the stories of Djerba into every carpet, crafting memories into every souvenir. Each piece carries the soul of Tunisia—the warmth of our sun, the richness of our culture, and the skill of our artisans.",
    about_p2_start: "We source materials locally, support traditional techniques, and ensure that every product you receive is ",
    about_p2_bold: "100% authentic",
    about_p2_end: ". From hand-knotted carpets to hand-painted ceramics, our collection celebrates the artistry that has defined Tunisian culture for centuries.",
    about_p3: "When you buy from us, you're not just purchasing a product—you're preserving a heritage and supporting local artisans.",
    about_stat1_num: "25+",
    about_stat1_label: "Years Experience",
    about_stat2_num: "500+",
    about_stat2_label: "Happy Customers",
    about_stat3_num: "100%",
    about_stat3_label: "Authentic",
    // Why Choose
    why_badge: "Why Choose Us",
    why_title: "Your Trust is Our Priority",
    why_desc: "We're committed to providing an exceptional shopping experience with authentic products, secure transactions, and outstanding customer service.",
    why_f1_title: "100% Authentic",
    why_f1_desc: "Every product is verified and sourced directly from local Tunisian artisans. No mass production, only genuine craftsmanship.",
    why_f2_title: "Handmade Quality",
    why_f2_desc: "Each piece is carefully crafted by skilled artisans using traditional techniques passed down through generations.",
    why_f3_title: "Worldwide Shipping",
    why_f3_desc: "We ship to over 50 countries with secure packaging and full tracking. International delivery in 7-14 days.",
    why_f4_title: "Secure Payment",
    why_f4_desc: "All transactions are encrypted and protected. We accept major credit cards, PayPal, and bank transfers.",
    why_f5_title: "24/7 Support",
    why_f5_desc: "Our team is available via WhatsApp, email, and phone to answer any questions and assist with your order.",
    why_f6_title: "Easy Returns",
    why_f6_desc: "Not satisfied? Return within 30 days for a full refund. We stand behind the quality of our products.",
    why_ssl: "SSL Encrypted",
    why_verified: "Verified Seller",
    why_free_ship: "Free Shipping $200+",
    // Products Section
    prod_section_badge: "Our Gallery",
    prod_section_title: "Handcrafted Tunisian Treasures",
    prod_section_desc: "Browse our curated collection of authentic pieces, each telling a unique story of Tunisian artistry and tradition.",
    prod_cat_all: "All Products",
    prod_cat_carpets: "Carpets",
    prod_cat_souvenirs: "Souvenirs",
    prod_cat_decor: "Decor",
    prod_cat_gifts: "Gifts",
    // Testimonials
    test_badge: "Customer Stories",
    test_title: "What Our Customers Say",
    test_desc: "Join hundreds of satisfied customers who have discovered authentic Tunisian treasures and fallen in love with our craftsmanship.",
    test_1_text: "Absolutely stunning carpet! The quality is incredible and the colors are even more vibrant in person. Shipping was fast and the packaging was excellent. Highly recommend!",
    test_1_product: "Berber Carpet",
    test_2_text: "I bought the ceramic bowl set as a gift and my wife loves it! The hand-painted details are exquisite. True Tunisian craftsmanship. Will definitely order again.",
    test_2_product: "Ceramic Bowl Set",
    test_3_text: "The brass lantern is a showstopper! Everyone who visits asks where I got it. The customer service was amazing—they answered all my questions via WhatsApp. Five stars!",
    test_3_product: "Brass Lantern",
    test_stat1: "Happy Customers",
    test_stat2: "Average Rating",
    test_stat3: "Countries Shipped",
    test_stat4: "Years Experience",
    // Gallery
    gallery_badge: "Gallery",
    gallery_title: "Real Adventures, Real Memories",
    gallery_desc: "See what awaits you in the stunning deserts of Djerba through the eyes of our guests.",
    gallery_follow: "Follow us on Instagram for daily updates",
    gallery_landscape: "Landscape",
    gallery_experience: "Experience",
  },
  fr: {
    nav_gallery: "Galerie",
    nav_adventures: "Aventures",
    nav_booking: "Réserver",
    nav_whatsapp: "WhatsApp",
    footer_contact_us: "Contactez-nous",
    footer_visit_us: "Visitez-nous",
    footer_location: "Localisation",
    footer_view_maps: "Voir sur Google Maps",
    footer_shop: "Boutique",
    footer_adventures: "Aventures",
    footer_info: "Infos",
    footer_legal: "Légal",
    footer_carpets: "Tapis",
    footer_souvenirs: "Souvenirs",
    footer_decor: "Décoration",
    footer_quad: "Tours en Quad",
    footer_buggy: "Tours en Buggy",
    footer_camel: "Balades à Dos de Chameau",
    footer_about: "À propos",
    footer_contact: "Contact",
    footer_faq: "FAQ",
    footer_shipping: "Livraison",
    footer_privacy: "Confidentialité",
    footer_terms: "Conditions",
    footer_rights: "Tous droits réservés.",
    footer_email_label: "E-mail",
    footer_phone_label: "Téléphone",
    booking_badge: "Réservez votre expérience",
    booking_title: "Réserver une aventure",
    booking_subtitle: "Remplissez le formulaire ci-dessous et nous confirmerons votre réservation rapidement.",
    booking_success_title: "Demande envoyée !",
    booking_success_msg: "Votre demande a été envoyée. Nous vous contacterons sous peu.",
    booking_another: "Faire une autre réservation",
    booking_fullname: "Nom complet",
    booking_email: "Adresse e-mail",
    booking_phone: "Numéro de téléphone",
    booking_persons: "Nombre de personnes",
    booking_activity: "Activité / Service sélectionné",
    booking_activity_placeholder: "— Choisir une activité —",
    booking_date: "Date souhaitée",
    booking_time: "Heure souhaitée",
    booking_notes: "Notes / Message supplémentaire",
    booking_notes_optional: "(facultatif)",
    booking_notes_placeholder: "Demandes spéciales, questions ou détails sur votre groupe...",
    booking_submit: "Envoyer la demande de réservation",
    booking_sending: "Envoi en cours...",
    booking_footer_note: "Nous vous répondrons dans les 24 heures pour confirmer votre réservation.",
    booking_land: "🏜️ Aventures Terrestres",
    booking_sea: "🌊 Activités Nautiques",
    booking_excursions: "🗺️ Excursions",
    err_fullname: "Le nom complet est requis.",
    err_email_required: "L\'adresse e-mail est requise.",
    err_email_invalid: "Veuillez entrer une adresse e-mail valide.",
    err_phone: "Le numéro de téléphone est requis.",
    err_activity: "Veuillez sélectionner une activité.",
    err_date: "La date souhaitée est requise.",
    err_time: "L\'heure souhaitée est requise.",
    adv_hero_title: "Explorez",
    adv_hero_subtitle: "Djerba",
    adv_hero_desc: "Des aventures désertiques inoubliables vous attendent.",
    adv_hero_desc_bold: " Tours en quad, buggy, treks à dos de chameau et équitation",
    adv_hero_desc_end: " encadrés par des guides experts.",
    adv_hero_book: "Réserver",
    adv_hero_whatsapp: "WhatsApp",
    adv_hero_guides: "Guides agréés",
    adv_hero_family: "Adapté aux familles",
    adv_hero_flexible: "Horaires flexibles",
    adv_hero_pickup: "Transfert hôtel",
    exp_badge: "Nos Aventures",
    exp_title: "Choisissez votre expérience",
    exp_desc: "Des randonnées désertiques palpitantes aux croisières marines apaisantes et aux excursions inoubliables — nous avons l'aventure parfaite pour chaque voyageur.",
    exp_book_now: "Réserver",
    exp_popular: "Populaire",
    exp_best_value: "Meilleur rapport qualité-prix",
    exp_unique: "Unique",
    exp_thrilling: "Sensationnel",
    exp_scenic: "Panoramique",
    exp_family_fun: "En famille",
    exp_recommended: "Recommandé",
    act_buggy: "Tours en Buggy",
    act_buggy_desc: "Dominez les dunes à bord d\'un buggy haute performance. Une balade pleine d\'adrénaline dans des paysages désertiques époustouflants.",
    act_horse: "Équitation",
    act_horse_desc: "Montez de majestueux chevaux arabes le long de plages immaculées et de sentiers pittoresques. Options pour tous les niveaux.",
    act_camel: "Balade à Dos de Chameau",
    act_camel_desc: "Voyagez comme les nomades d'antan lors d'un paisible trek à dos de chameau dans le Sahara. Idéal pour les familles et la photographie au coucher du soleil.",
    act_quad: "Tours en Quad",
    act_quad_desc: "Vivez le frisson de la conduite de quads puissants à travers des paysages désertiques et des villages berbères.",
    act_caleche: "Calèche (Voiture à Cheval)",
    act_caleche_desc: "Explorez les sites locaux dans une charmante calèche traditionnelle. Visitez le parc aux crocodiles, la lagune et le marché.",
    act_caleche_horse: "Calèche + Expérience Équestre",
    act_caleche_horse_desc: "Une expérience unique combinant une balade en calèche, un repas et la nage avec les chevaux. Inoubliable !",
    act_jetski: "Jet Ski",
    act_jetski_desc: "Ressentez l\'ivresse de la Méditerranée sur un jet ski puissant. Options solo ou partagées disponibles.",
    act_boat: "Balade en Bateau",
    act_boat_desc: "Profitez d'une balade en bateau relaxante le long du magnifique littoral de Djerba avec de superbes vues sur la mer.",
    act_parasailing: "Parachute Ascensionnel",
    act_parasailing_desc: "Planez au-dessus de la Méditerranée et profitez de vues aériennes à couper le souffle sur le littoral de Djerba.",
    act_water: "Jeux Nautiques",
    act_water_desc: "Activités aquatiques pour tous les âges — banane, bouées gonflables et plus encore. Parfait pour les groupes et les familles.",
    act_pirate: "Bateau Pirate",
    act_pirate_desc: "Moussaillon ! Embarquez pour une croisière pirate amusante le long des côtes de Djerba. Idéal pour les familles et les groupes.",
    act_island: "Tour de l\'Île",
    act_island_desc: "Découvrez les points forts de la belle île de Djerba lors d\'une visite guidée couvrant les sites clés et les joyaux cachés.",
    act_ksar: "Excursion à Ksar Ghilane",
    act_ksar_desc: "Plongez au cœur du Sahara jusqu\'à la légendaire oasis de Ksar Ghilane. Option nuit sous tente avec dîner au coucher du soleil.",
    act_tataouine: "Voyage à Tataouine",
    act_tataouine_desc: "Explorez les anciens ksour berbères (greniers fortifiés) et les paysages dramatiques de Tataouine — un véritable voyage dans le temps.",
    price_individual: "Individuel (1h30)",
    price_normal: "Balade normale (1h)",
    price_pro: "Balade professionnelle (2h30)",
    price_croc: "Parc aux Crocodiles",
    price_lagoon: "Lagune",
    price_market: "Marché",
    price_per_person: "Par personne",
    price_per_person_min2: "Par personne (min. 2)",
    price_standard: "Standard",
    price_tent: "Avec tente + dîner au coucher du soleil",
    price_30min: "30 min",
    price_contact: "Nous contacter",
    note_thu: "Départ jeudi : 14h00",
    note_fri: "Départ vendredi : 08h45",
    note_dep: "Départs : 08h45 & 15h30",
    note_meal: "Inclut repas + nage avec les chevaux",
    note_min2: "Minimum 2 personnes",
    sec_land: "Aventures Terrestres",
    sec_sea: "Activités Nautiques",
    sec_excursions: "Excursions",
    htb_badge: "Processus Simple",
    htb_title: "Comment réserver votre aventure",
    htb_desc: "La réservation est rapide et facile. Suivez ces étapes simples pour sécuriser votre expérience désertique.",
    htb_step: "Étape",
    htb_ready: "Prêt à commencer ?",
    htb_whatsapp: "Réserver via WhatsApp",
    htb_s1_title: "Choisir l\'expérience",
    htb_s1_desc: "Choisissez votre aventure préférée parmi nos options de quad, buggy, chameau ou équitation.",
    htb_s2_title: "Choisir la date et l\'heure",
    htb_s2_desc: "Sélectionnez votre date et créneau horaire préférés. Options matin, après-midi et coucher de soleil disponibles.",
    htb_s3_title: "Confirmer les détails",
    htb_s3_desc: "Indiquez le nombre de personnes, l'hôtel pour le transfert et toute demande spéciale.",
    htb_s4_title: "Profiter de l\'aventure",
    htb_s4_desc: "Nous vous récupérons à votre hôtel. Préparez-vous pour une expérience désertique inoubliable !",
    faq_badge: "FAQ",
    faq_title: "Questions Fréquemment Posées",
    faq_desc: "Tout ce que vous devez savoir avant de réserver votre aventure désertique. Vous ne trouvez pas votre réponse ? Contactez-nous via WhatsApp.",
    faq_still: "Vous avez encore des questions ?",
    faq_chat: "Chattez avec nous sur WhatsApp",
    faq_q1: "Quelles sont les conditions d\'âge ?",
    faq_a1: "Les tours en quad et buggy nécessitent que les conducteurs aient 18 ans et un permis valide. Les passagers peuvent avoir 12 ans et plus. Les balades à dos de chameau et à cheval conviennent à tous les âges (les enfants de moins de 10 ans doivent être accompagnés d\'un adulte).",
    faq_q2: "Ai-je besoin d\'une expérience préalable ?",
    faq_a2: "Aucune expérience nécessaire ! Toutes les aventures comprennent un briefing de sécurité et une formation de base. Nos guides professionnels vous apprendront tout ce que vous devez savoir. Nous accueillons débutants et cavaliers expérimentés.",
    faq_q3: "Que dois-je apporter ?",
    faq_a3: "Des chaussures fermées confortables, des lunettes de soleil, de la crème solaire et un appareil photo. Nous fournissons tout l'équipement de sécurité (casques, équipement de protection). Des vêtements légers et un foulard pour se protéger de la poussière sont recommandés.",
    faq_q4: "Que se passe-t-il en cas de mauvais temps ?",
    faq_a4: "La sécurité avant tout ! Si les conditions météorologiques sont dangereuses, nous reprogrammerons votre aventure ou vous rembourserons intégralement. Nous surveillons la météo de près et vous contacterons à l'avance si des changements sont nécessaires.",
    faq_q5: "Quelle est votre politique d\'annulation ?",
    faq_a5: "Annulation gratuite jusqu\'à 24 heures avant votre aventure. Les annulations dans les 24 heures sont soumises à des frais de 50 %. Les non-présentations ne sont pas remboursables. Nous sommes flexibles et travaillerons avec vous pour reprogrammer.",
    faq_q6: "Proposez-vous un transfert depuis l\'hôtel ?",
    faq_a6: "Oui ! Le transfert depuis l'hôtel est inclus pour toutes les aventures. Nous couvrons la plupart des hôtels de Djerba. Veuillez indiquer le nom de votre hôtel lors de la réservation et nous confirmerons l'heure de prise en charge (généralement 30 à 60 minutes avant le début).",
    faq_q7: "Puis-je réserver pour un grand groupe ?",
    faq_a7: "Absolument ! Nous sommes spécialisés dans les réservations de groupe pour les familles, les événements d'entreprise et les agences de voyage. Les groupes de 10 personnes et plus bénéficient de remises spéciales. Contactez-nous via WhatsApp pour des forfaits groupes personnalisés.",
    faq_q8: "Quels modes de paiement acceptez-vous ?",
    faq_a8: "Nous acceptons les espèces (TND, USD, EUR), les cartes de crédit (Visa, Mastercard) et les virements bancaires. Un acompte de 30 % sécurise votre réservation, le solde étant dû le jour de votre aventure.",
    shop_hero_title: "Artisanat",
    shop_hero_subtitle: "Tunisien Authentique",
    shop_hero_desc: "Découvrez des trésors faits main au cœur de Djerba.",
    shop_hero_desc_bold: " Tapis traditionnels, souvenirs uniques,",
    shop_hero_desc_end: " et expériences inoubliables.",
    shop_hero_explore: "Explorer la Galerie",
    shop_hero_book: "Réserver une Expérience",
    shop_hero_authentic: "100% Authentique",
    shop_hero_shipping: "Livraison Mondiale",
    shop_hero_customers: "500+ Clients Satisfaits",
    about_badge: "Notre Patrimoine",
    about_title: "Préserver l\'Artisanat Tunisien pour les Générations Futures",
    about_p1: "Depuis plus de trois générations, notre famille tisse les histoires de Djerba dans chaque tapis, créant des souvenirs dans chaque objet artisanal. Chaque pièce porte l'âme de la Tunisie — la chaleur de notre soleil, la richesse de notre culture et le savoir-faire de nos artisans.",
    about_p2_start: "Nous nous approvisionnons localement, soutenons les techniques traditionnelles et veillons à ce que chaque produit que vous recevez soit ",
    about_p2_bold: "100% authentique",
    about_p2_end: ". Des tapis noués à la main aux céramiques peintes à la main, notre collection célèbre l'art qui a défini la culture tunisienne depuis des siècles.",
    about_p3: "En achetant chez nous, vous n'achetez pas seulement un produit — vous préservez un patrimoine et soutenez les artisans locaux.",
    about_stat1_num: "25+",
    about_stat1_label: "Ans d\'expérience",
    about_stat2_num: "500+",
    about_stat2_label: "Clients satisfaits",
    about_stat3_num: "100%",
    about_stat3_label: "Authentique",
    why_badge: "Pourquoi nous choisir",
    why_title: "Votre confiance est notre priorité",
    why_desc: "Nous nous engageons à offrir une expérience d'achat exceptionnelle avec des produits authentiques, des transactions sécurisées et un service client remarquable.",
    why_f1_title: "100% Authentique",
    why_f1_desc: "Chaque produit est vérifié et sourcé directement auprès des artisans tunisiens locaux. Pas de production de masse, uniquement un artisanat authentique.",
    why_f2_title: "Qualité Artisanale",
    why_f2_desc: "Chaque pièce est soigneusement fabriquée par des artisans qualifiés utilisant des techniques traditionnelles transmises de génération en génération.",
    why_f3_title: "Livraison Mondiale",
    why_f3_desc: "Nous expédions dans plus de 50 pays avec un emballage sécurisé et un suivi complet. Livraison internationale en 7 à 14 jours.",
    why_f4_title: "Paiement Sécurisé",
    why_f4_desc: "Toutes les transactions sont cryptées et protégées. Nous acceptons les principales cartes de crédit, PayPal et les virements bancaires.",
    why_f5_title: "Support 24h/24",
    why_f5_desc: "Notre équipe est disponible via WhatsApp, e-mail et téléphone pour répondre à vos questions et vous aider avec votre commande.",
    why_f6_title: "Retours Faciles",
    why_f6_desc: "Pas satisfait ? Retournez dans les 30 jours pour un remboursement complet. Nous garantissons la qualité de nos produits.",
    why_ssl: "Cryptage SSL",
    why_verified: "Vendeur Vérifié",
    why_free_ship: "Livraison Gratuite dès 200$",
    // Products Section
    prod_section_badge: "Notre Galerie",
    prod_section_title: "Trésors Tunisiens yéna de la main",
    prod_section_desc: "Parcourez notre collection de pièces authentiques, chacune racontant une histoire unique de l'artisanat tunisien.",
    prod_cat_all: "Tous les produits",
    prod_cat_carpets: "Tapis",
    prod_cat_souvenirs: "Souvenirs",
    prod_cat_decor: "Décoration",
    prod_cat_gifts: "Cadeaux",
    // Testimonials
    test_badge: "Témoignages clients",
    test_title: "Ce que disent nos clients",
    test_desc: "Rejoignez des centaines de clients satisfaits qui ont découvert des trésors tunisiens authentiques et sont tombés amoureux de notre artisanat.",
    test_1_text: "Tapis absolument magnifique ! La qualité est incroyable et les couleurs sont encore plus vibrantes en personne. Livraison rapide et emballage excellent. Hautement recommandé !",
    test_1_product: "Tapis Berbère",
    test_2_text: "J'ai acheté le service de bols en céramique comme cadeau et ma femme le adore ! Les détails peints à la main sont exquis. Véritable artisanat tunisien. Je commanderai certainement à nouveau.",
    test_2_product: "Service de bols en céramique",
    test_3_text: "La lanterne en laiton est époustouflante ! Tous les visiteurs demandent où je l\'ai trouvée. Le service client était incroyable — ils ont répondu à toutes mes questions via WhatsApp. Cinq étoiles !",
    test_3_product: "Lanterne en laiton",
    test_stat1: "Clients satisfaits",
    test_stat2: "Note moyenne",
    test_stat3: "Pays livrés",
    test_stat4: "Ans d\'expérience",
    // Gallery
    gallery_badge: "Galerie",
    gallery_title: "Vraies Aventures, Vrais Souvenirs",
    gallery_desc: "Découvrez ce qui vous attend dans les déserts époustouflants de Djerba à travers les yeux de nos clients.",
    gallery_follow: "Suivez-nous sur Instagram pour des mises à jour quotidiennes",
    gallery_landscape: "Paysage",
    gallery_experience: "Expérience",
  },
  ar: {
    nav_gallery: "المعرض",
    nav_adventures: "المغامرات",
    nav_booking: "احجز الآن",
    nav_whatsapp: "واتساب",
    footer_contact_us: "اتصل بنا",
    footer_visit_us: "زورونا",
    footer_location: "Location",
    footer_view_maps: "View on Google Maps",
    footer_shop: "المتجر",
    footer_adventures: "المغامرات",
    footer_info: "معلومات",
    footer_legal: "قانوني",
    footer_carpets: "السجاد",
    footer_souvenirs: "الهدايا التذكارية",
    footer_decor: "الديكور",
    footer_quad: "جولات الكواد",
    footer_buggy: "جولات البوجي",
    footer_camel: "ركوب الجمال",
    footer_about: "من نحن",
    footer_contact: "اتصل بنا",
    footer_faq: "الأسئلة الشائعة",
    footer_shipping: "الشحن",
    footer_privacy: "الخصوصية",
    footer_terms: "الشروط",
    footer_rights: "جميع الحقوق محفوظة.",
    footer_email_label: "البريد الإلكتروني",
    footer_phone_label: "الهاتف",
    booking_badge: "احجز تجربتك",
    booking_title: "احجز مغامرة",
    booking_subtitle: "املأ النموذج أدناه وسنؤكد حجزك في أقرب وقت.",
    booking_success_title: "تم إرسال الطلب!",
    booking_success_msg: "تم إرسال طلبك. سنتواصل معك قريباً.",
    booking_another: "إجراء حجز آخر",
    booking_fullname: "الاسم الكامل",
    booking_email: "عنوان البريد الإلكتروني",
    booking_phone: "رقم الهاتف",
    booking_persons: "عدد الأشخاص",
    booking_activity: "النشاط / الخدمة المختارة",
    booking_activity_placeholder: "— اختر نشاطاً —",
    booking_date: "التاريخ المفضل",
    booking_time: "الوقت المفضل",
    booking_notes: "ملاحظات / رسالة إضافية",
    booking_notes_optional: "(اختياري)",
    booking_notes_placeholder: "أي طلبات خاصة أو أسئلة أو تفاصيل حول مجموعتك...",
    booking_submit: "إرسال طلب الحجز",
    booking_sending: "جارٍ الإرسال...",
    booking_footer_note: "سنرد عليك خلال 24 ساعة لتأكيد حجزك.",
    booking_land: "🏜️ مغامرات برية",
    booking_sea: "🌊 أنشطة بحرية",
    booking_excursions: "🗺️ رحلات",
    err_fullname: "الاسم الكامل مطلوب.",
    err_email_required: "عنوان البريد الإلكتروني مطلوب.",
    err_email_invalid: "يرجى إدخال عنوان بريد إلكتروني صالح.",
    err_phone: "رقم الهاتف مطلوب.",
    err_activity: "يرجى اختيار نشاط.",
    err_date: "التاريخ المفضل مطلوب.",
    err_time: "الوقت المفضل مطلوب.",
    adv_hero_title: "استكشف",
    adv_hero_subtitle: "جربة",
    adv_hero_desc: "مغامرات صحراوية لا تُنسى في انتظارك.",
    adv_hero_desc_bold: " جولات الكواد، البوجي، رحلات الجمال وركوب الخيل",
    adv_hero_desc_end: " بإشراف مرشدين خبراء.",
    adv_hero_book: "احجز الآن",
    adv_hero_whatsapp: "واتساب",
    adv_hero_guides: "مرشدون معتمدون",
    adv_hero_family: "مناسب للعائلات",
    adv_hero_flexible: "أوقات مرنة",
    adv_hero_pickup: "نقل من الفندق",
    exp_badge: "مغامراتنا",
    exp_title: "اختر تجربتك",
    exp_desc: "From thrilling desert rides to peaceful sea cruises and unforgettable excursions — we have the perfect adventure for every traveller.",
    exp_book_now: "احجز الآن",
    exp_popular: "الأكثر شعبية",
    exp_best_value: "أفضل قيمة",
    exp_unique: "فريد",
    exp_thrilling: "مثير",
    exp_scenic: "خلاب",
    exp_family_fun: "مرح عائلي",
    exp_recommended: "موصى به",
    act_buggy: "جولات البوجي",
    act_buggy_desc: "اقتحم الكثبان الرملية على متن بوجي عالي الأداء. رحلة مليئة بالأدرينالين عبر مناظر صحراوية خلابة.",
    act_horse: "ركوب الخيل",
    act_horse_desc: "اركب خيولاً عربية رائعة على طول الشواطئ البكر والمسارات الجميلة. خيارات لجميع مستويات المهارة.",
    act_camel: "جولة الجمال",
    act_camel_desc: "سافر كالرحالة القدامى في رحلة جمل هادئة عبر الصحراء الكبرى. مثالية للعائلات والتصوير عند غروب الشمس.",
    act_quad: "جولات الكواد",
    act_quad_desc: "عش إثارة قيادة دراجات الكواد القوية عبر المناظر الصحراوية الخلابة والقرى البربرية.",
    act_caleche: "العربة (عربة الخيل)",
    act_caleche_desc: "استكشف المعالم المحلية في عربة خيل تقليدية ساحرة. قم بزيارة حديقة التماسيح والبحيرة والسوق.",
    act_caleche_horse: "العربة + تجربة الخيل",
    act_caleche_horse_desc: "تجربة فريدة تجمع بين ركوب عربة الخيل ووجبة والسباحة مع الخيول. لا تُنسى!",
    act_jetski: "جت سكي",
    act_jetski_desc: "أحسس بإثارة البحر الأبيض المتوسط على متن جت سكي قوي. خيارات فردية أو مشتركة متاحة.",
    act_boat: "رحلة بالقارب",
    act_boat_desc: "استمتع برحلة قارب مريحة على طول الساحل الجميل لتجربة مع مناظر بحرية رائعة.",
    act_parasailing: "الباراسيلينج",
    act_parasailing_desc: "حلق فوق البحر الأبيض المتوسط واستمتع بمناظر جوية خلابة لساحل جربة.",
    act_water: "الألعاب المائية",
    act_water_desc: "أنشطة مائية ممتعة لجميع الأعمار — قوارب الموز والعوامات وأكثر. مثالية للمجموعات والعائلات.",
    act_pirate: "قارب القراصنة",
    act_pirate_desc: "أهلاً أيها البحار! انطلق في رحلة بحرية على طراز القراصنة على طول ساحل جربة. رائع للعائلات والمجموعات.",
    act_island: "جولة الجزيرة",
    act_island_desc: "اكتشف أبرز معالم جزيرة جربة الجميلة في جولة مرشدة تغطي المواقع الرئيسية والجواهر الخفية.",
    act_ksar: "رحلة قصر قيلان",
    act_ksar_desc: "انطلق في رحلة إلى أعماق الصحراء نحو واحة قصر قيلان الأسطورية. خيار المبيت في خيمة مع عشاء عند غروب الشمس.",
    act_tataouine: "رحلة تطاوين",
    act_tataouine_desc: "استكشف القصور البربرية القديمة (المخازن المحصنة) والمناظر الطبيعية الدرامية لتطاوين — رحلة حقيقية عبر الزمن.",
    price_individual: "فردي (1س30د)",
    price_normal: "ركوب عادي (1س)",
    price_pro: "ركوب احترافي (2س30د)",
    price_croc: "حديقة التماسيح",
    price_lagoon: "البحيرة",
    price_market: "السوق",
    price_per_person: "للشخص",
    price_per_person_min2: "للشخص (حد أدنى 2)",
    price_standard: "قياسي",
    price_tent: "مع خيمة + عشاء عند الغروب",
    price_30min: "30 دقيقة",
    price_contact: "تواصل معنا",
    note_thu: "مغادرة الخميس: 14:00",
    note_fri: "مغادرة الجمعة: 08:45",
    note_dep: "المغادرات: 08:45 و15:30",
    note_meal: "يشمل وجبة + السباحة مع الخيول",
    note_min2: "الحد الأدنى شخصان",
    sec_land: "المغامرات البرية",
    sec_sea: "الأنشطة البحرية",
    sec_excursions: "الرحلات",
    htb_badge: "عملية بسيطة",
    htb_title: "كيفية حجز مغامرتك",
    htb_desc: "الحجز سريع وسهل. اتبع هذه الخطوات البسيطة لتأمين تجربتك الصحراوية.",
    htb_step: "الخطوة",
    htb_ready: "مستعد للبدء؟",
    htb_whatsapp: "احجز عبر واتساب",
    htb_s1_title: "اختر التجربة",
    htb_s1_desc: "اختر مغامرتك المفضلة من بين خيارات الكواد والبوجي والجمل وركوب الخيل.",
    htb_s2_title: "اختر التاريخ والوقت",
    htb_s2_desc: "حدد تاريخك ووقتك المفضلين. خيارات الصباح والظهيرة وغروب الشمس متاحة.",
    htb_s3_title: "تأكيد التفاصيل",
    htb_s3_desc: "أدخل عدد الأشخاص وموقع الفندق للنقل وأي طلبات خاصة.",
    htb_s4_title: "استمتع بالمغامرة",
    htb_s4_desc: "سنستقبلك من فندقك. استعد لتجربة صحراوية لا تُنسى!",
    faq_badge: "الأسئلة الشائعة",
    faq_title: "الأسئلة المتكررة",
    faq_desc: "كل ما تحتاج معرفته قبل حجز مغامرتك الصحراوية. لم تجد إجابتك؟ تواصل معنا عبر واتساب.",
    faq_still: "هل لا تزال لديك أسئلة؟",
    faq_chat: "تحدث معنا على واتساب",
    faq_q1: "ما هي متطلبات العمر؟",
    faq_a1: "تتطلب جولات الكواد والبوجي أن يكون السائقون بعمر 18 عاماً فأكثر مع رخصة قيادة سارية. يمكن للركاب أن يكونوا بعمر 12 عاماً فأكثر. ركوب الجمال والخيل مناسب لجميع الأعمار (يجب أن يكون الأطفال دون سن 10 سنوات برفقة بالغ).",
    faq_q2: "هل أحتاج إلى خبرة مسبقة؟",
    faq_a2: "لا حاجة لأي خبرة! تتضمن جميع المغامرات إحاطة أمنية وتدريباً أساسياً. سيعلمك مرشدونا المحترفون كل ما تحتاج معرفته. نستقبل المبتدئين والفرسان ذوي الخبرة على حد سواء.",
    faq_q3: "ماذا يجب أن أحضر؟",
    faq_a3: "حذاء مريح مغلق الأصابع، نظارات شمسية، واقي شمس وكاميرا. نوفر جميع معدات السلامة (خوذات، معدات واقية). يُنصح بارتداء ملابس خفيفة ووشاح للحماية من الغبار.",
    faq_q4: "ماذا لو كان الطقس سيئاً؟",
    faq_a4: "السلامة أولاً! إذا كانت الظروف الجوية غير آمنة، سنعيد جدولة مغامرتك أو نقدم استرداداً كاملاً. نراقب الطقس عن كثب وسنتواصل معك مسبقاً إذا كانت هناك تغييرات ضرورية.",
    faq_q5: "ما هي سياسة الإلغاء لديكم؟",
    faq_a5: "إلغاء مجاني حتى 24 ساعة قبل مغامرتك. تخضع الإلغاءات خلال 24 ساعة لرسوم بنسبة 50٪. حالات عدم الحضور غير قابلة للاسترداد. نحن مرنون وسنعمل معك على إعادة الجدولة.",
    faq_q6: "هل تقدمون نقلاً من الفندق؟",
    faq_a6: "نعم! النقل من الفندق مشمول في جميع المغامرات. نغطي معظم فنادق جربة. يرجى تقديم اسم فندقك عند الحجز وسنؤكد وقت الاستلام (عادةً 30-60 دقيقة قبل وقت البدء).",
    faq_q7: "هل يمكنني الحجز لمجموعة كبيرة؟",
    faq_a7: "بالتأكيد! نحن متخصصون في حجوزات المجموعات للعائلات والفعاليات الشركاتية ووكالات السفر. تحصل المجموعات المكونة من 10 أشخاص وأكثر على خصومات خاصة. تواصل معنا عبر واتساب للحصول على باقات مجموعات مخصصة.",
    faq_q8: "ما طرق الدفع التي تقبلونها؟",
    faq_a8: "نقبل النقد (دينار تونسي، دولار أمريكي، يورو)، بطاقات الائتمان (فيزا، ماستركارد) والتحويلات المصرفية. يؤمن دفعة مقدمة بنسبة 30٪ حجزك، مع سداد الرصيد في يوم مغامرتك.",
    shop_hero_title: "الحرف",
    shop_hero_subtitle: "التونسية الأصيلة",
    shop_hero_desc: "اكتشف كنوزاً يدوية الصنع من قلب جربة.",
    shop_hero_desc_bold: " سجاد تقليدي، هدايا تذكارية فريدة،",
    shop_hero_desc_end: " وتجارب لا تُنسى.",
    shop_hero_explore: "استكشف المعرض",
    shop_hero_book: "احجز تجربة",
    shop_hero_authentic: "100% أصيل",
    shop_hero_shipping: "شحن عالمي",
    shop_hero_customers: "+500 عميل سعيد",
    about_badge: "تراثنا",
    about_title: "الحفاظ على الحرف التونسية للأجيال القادمة",
    about_p1: "لأكثر من ثلاثة أجيال، كانت عائلتنا تنسج قصص جربة في كل سجادة، وتصنع الذكريات في كل هدية تذكارية. كل قطعة تحمل روح تونس — دفء شمسنا وثراء ثقافتنا ومهارة حرفيينا.",
    about_p2_start: "نحصل على المواد محلياً، وندعم التقنيات التقليدية، ونضمن أن كل منتج تتلقاه هو ",
    about_p2_bold: "100% أصيل",
    about_p2_end: ". من السجاد المعقد يدوياً إلى السيراميك المرسوم يدوياً، تحتفل مجموعتنا بالفن الذي حدد الثقافة التونسية لقرون.",
    about_p3: "عندما تشتري منا، فأنت لا تشتري منتجاً فحسب — بل تحافظ على تراث وتدعم الحرفيين المحليين.",
    about_stat1_num: "+25",
    about_stat1_label: "سنة خبرة",
    about_stat2_num: "+500",
    about_stat2_label: "عميل سعيد",
    about_stat3_num: "100%",
    about_stat3_label: "أصيل",
    why_badge: "لماذا تختارنا",
    why_title: "ثقتك هي أولويتنا",
    why_desc: "نلتزم بتقديم تجربة تسوق استثنائية بمنتجات أصيلة ومعاملات آمنة وخدمة عملاء متميزة.",
    why_f1_title: "100% أصيل",
    why_f1_desc: "كل منتج موثق ومصدره مباشرة من الحرفيين التونسيين المحليين. لا إنتاج ضخم، فقط حرفية حقيقية.",
    why_f2_title: "جودة يدوية",
    why_f2_desc: "كل قطعة مصنوعة بعناية من قبل حرفيين مهرة باستخدام تقنيات تقليدية متوارثة عبر الأجيال.",
    why_f3_title: "شحن عالمي",
    why_f3_desc: "نشحن إلى أكثر من 50 دولة بتغليف آمن وتتبع كامل. التسليم الدولي في 7-14 يوماً.",
    why_f4_title: "دفع آمن",
    why_f4_desc: "جميع المعاملات مشفرة ومحمية. نقبل بطاقات الائتمان الرئيسية وباي بال والتحويلات المصرفية.",
    why_f5_title: "دعم على مدار الساعة",
    why_f5_desc: "فريقنا متاح عبر واتساب والبريد الإلكتروني والهاتف للإجابة على أسئلتك ومساعدتك في طلبك.",
    why_f6_title: "إرجاع سهل",
    why_f6_desc: "غير راضٍ؟ أعد خلال 30 يوماً لاسترداد كامل. نضمن جودة منتجاتنا.",
    why_ssl: "تشفير SSL",
    why_verified: "بائع موثق",
    why_free_ship: "شحن مجاني عند +200$",
    // Products Section
    prod_section_badge: "معرضنا",
    prod_section_title: "كنوز تونسية يدوية الصنع",
    prod_section_desc: "تصفح مجموعتنا المنتقاة من القطع الأصيلة، كل منها يحكي قصة فريدة من الفن التونسي.",
    prod_cat_all: "جميع المنتجات",
    prod_cat_carpets: "السجاد",
    prod_cat_souvenirs: "الهدايا التذكارية",
    prod_cat_decor: "الديكور",
    prod_cat_gifts: "الهدايا",
    // Testimonials
    test_badge: "قصص العملاء",
    test_title: "Co říkají naši zákazníci",
    test_desc: "Připojte se ke stovkám spokojených zákazníků, kteří objevili autentické tuniské poklady a zamilovali se do našeho řemesla.",
    test_1_text: "Absolutně úžasný koberec! Jakość je niesamowita, a barvy jsou osobně ještě živější. Doprava byla rychlá, a balení vynikající. Vřele doporučuji!",
    test_1_product: "Berberský Koberec",
    test_2_text: "Koupil jsem sadu keramických misek jako dárek a moje žena ji miluje! Ručně malované detaily jsou nádherné. Skutečné tuniské řemeslo. Určitě objednám znovu.",
    test_2_product: "Sada Keramických Misek",
    test_3_text: "Mosazná lucerna je úžasná! Každý návštěvník se ptá, kde jsem ji vzal. Zákaznický servis byl úžasný — odpověděli na všechny mé otázky přes WhatsApp. Pět hvězdiček!",
    test_3_product: "Mosazná Lucerna",
    test_stat1: "Spokojených Zákazníků",
    test_stat2: "Průměrné Hodnocení",
    test_stat3: "Zemí Doručení",
    test_stat4: "Let Zkušeností",
    // Gallery
    gallery_badge: "Galerie",
    gallery_title: "Skutečná Dobrodružství, Skutečné Vzpomínky",
    gallery_desc: "Podívejte se, co na vás čeká v úžasných pouštích Džerby očima našich hostů.",
    gallery_follow: "Sledujte nás na Instagramu pro denní aktualizace",
    gallery_landscape: "Krajina",
    gallery_experience: "Zážitek",
  },
  pl: {
    nav_gallery: "Galeria",
    nav_adventures: "Przygody",
    nav_booking: "Zarezerwuj",
    nav_whatsapp: "WhatsApp",
    footer_contact_us: "Skontaktuj się z nami",
    footer_visit_us: "Odwiedź nas",
    footer_location: "Lokalizacja",
    footer_view_maps: "Zobacz na Google Maps",
    footer_shop: "Sklep",
    footer_adventures: "Przygody",
    footer_info: "Informacje",
    footer_legal: "Prawne",
    footer_carpets: "Dywany",
    footer_souvenirs: "Pamiątki",
    footer_decor: "Dekoracje",
    footer_quad: "Wycieczki quadem",
    footer_buggy: "Wycieczki buggy",
    footer_camel: "Przejażdżki na wielbłądzie",
    footer_about: "O nas",
    footer_contact: "Kontakt",
    footer_faq: "FAQ",
    footer_shipping: "Wysyłka",
    footer_privacy: "Prywatność",
    footer_terms: "Warunki",
    footer_rights: "Wszelkie prawa zastrzeżone.",
    footer_email_label: "E-mail",
    footer_phone_label: "Telefon",
    booking_badge: "Zarezerwuj swoje doświadczenie",
    booking_title: "Zarezerwuj przygodę",
    booking_subtitle: "Wypełnij poniższy formularz, a my wkrótce potwierdzimy Twoją rezerwację.",
    booking_success_title: "Zapytanie wysłane!",
    booking_success_msg: "Twoje zapytanie zostało wysłane. Wkrótce się z Tobą skontaktujemy.",
    booking_another: "Dokonaj kolejnej rezerwacji",
    booking_fullname: "Imię i nazwisko",
    booking_email: "Adres e-mail",
    booking_phone: "Numer telefonu",
    booking_persons: "Liczba osób",
    booking_activity: "Wybrana aktywność / usługa",
    booking_activity_placeholder: "— Wybierz aktywność —",
    booking_date: "Preferowana data",
    booking_time: "Preferowana godzina",
    booking_notes: "Dodatkowe uwagi / wiadomość",
    booking_notes_optional: "(opcjonalnie)",
    booking_notes_placeholder: "Specjalne życzenia, pytania lub szczegóły dotyczące Twojej grupy...",
    booking_submit: "Wyślij zapytanie o rezerwację",
    booking_sending: "Wysyłanie...",
    booking_footer_note: "Odpowiemy w ciągu 24 godzin, aby potwierdzić Twoją rezerwację.",
    booking_land: "🏜️ Przygody lądowe",
    booking_sea: "🌊 Aktywności wodne",
    booking_excursions: "🗺️ Wycieczki",
    err_fullname: "Imię i nazwisko jest wymagane.",
    err_email_required: "Adres e-mail jest wymagany.",
    err_email_invalid: "Proszę podać prawidłowy adres e-mail.",
    err_phone: "Numer telefonu jest wymagany.",
    err_activity: "Proszę wybrać aktywność.",
    err_date: "Preferowana data jest wymagana.",
    err_time: "Preferowana godzina jest wymagana.",
    adv_hero_title: "Odkryj",
    adv_hero_subtitle: "Dżerbę",
    adv_hero_desc: "Czekają na Ciebie niezapomniane przygody na pustyni.",
    adv_hero_desc_bold: " Wycieczki quadem, buggy, przejażdżki na wielbłądzie i konno",
    adv_hero_desc_end: " pod okiem doświadczonych przewodników.",
    adv_hero_book: "Zarezerwuj",
    adv_hero_whatsapp: "WhatsApp",
    adv_hero_guides: "Licencjonowani przewodnicy",
    adv_hero_family: "Przyjazne rodzinom",
    adv_hero_flexible: "Elastyczne godziny",
    adv_hero_pickup: "Transfer z hotelu",
    exp_badge: "Nasze przygody",
    exp_title: "Wybierz swoje doświadczenie",
    exp_desc: "Od ekscytujących przejażdżek po pustyni po spokojne rejsy morskie i niezapomniane wycieczki — mamy idealną przygodę dla każdego podróżnika.",
    exp_book_now: "Zarezerwuj",
    exp_popular: "Popularne",
    exp_best_value: "Najlepsza wartość",
    exp_unique: "Wyjątkowe",
    exp_thrilling: "Ekscytujące",
    exp_scenic: "Malownicze",
    exp_family_fun: "Rodzinna zabawa",
    exp_recommended: "Polecane",
    act_buggy: "Wycieczki buggy",
    act_buggy_desc: "Pokonaj wydmy w wysokowydajnym buggy. Pełna adrenaliny przejażdżka przez zapierające dech w piersiach pustynne krajobrazy.",
    act_horse: "Jazda konna",
    act_horse_desc: "Jedź na majestatycznych arabskich koniach wzdłuż dziewiczych plaż i malowniczych szlaków. Opcje dla wszystkich poziomów umiejętności.",
    act_camel: "Wycieczka na wielbłądzie",
    act_camel_desc: "Podróżuj jak starożytni nomadzi na spokojnym trekku na wielbłądzie przez Saharę. Idealne dla rodzin i fotografii o zachodzie słońca.",
    act_quad: "Wycieczki quadem",
    act_quad_desc: "Poczuj dreszczyk emocji jazdy potężnymi quadami przez zapierające dech pustynne krajobrazy i berberyjskie wioski.",
    act_caleche: "Kalesze (Powóz konny)",
    act_caleche_desc: "Odkryj lokalne atrakcje w uroczym tradycyjnym powozie konnym. Odwiedź park krokodyli, lagunę i targ.",
    act_caleche_horse: "Kalesze + Doświadczenie z koniem",
    act_caleche_horse_desc: "Wyjątkowe połączone doświadczenie obejmujące przejazd powozem, posiłek i pływanie z końmi. Niezapomniane!",
    act_jetski: "Skuter wodny",
    act_jetski_desc: "Poczuj dreszczyk Morza Śródziemnego na potężnym skuterze wodnym. Dostępne opcje solo lub wspólne.",
    act_boat: "Rejs łodzią",
    act_boat_desc: "Ciesz się relaksującym rejsem łodzią wzdłuż pięknego wybrzeża Dżerby z zapierającymi dech widokami na morze.",
    act_parasailing: "Parasailing",
    act_parasailing_desc: "Szybuj nad Morzem Śródziemnym i ciesz się zapierającymi dech widokami z lotu ptaka na wybrzeże Dżerby.",
    act_water: "Gry wodne",
    act_water_desc: "Pełne zabawy aktywności wodne dla wszystkich grup wiekowych — banany, dmuchańce i wiele więcej. Idealne dla grup i rodzin.",
    act_pirate: "Statek piratów",
    act_pirate_desc: "Ahoj! Wyrusz na zabawny rejs w stylu piratów wzdłuż wybrzeża Dżerba. Świetne dla rodzin i grup.",
    act_island: "Wycieczka po wyspie",
    act_island_desc: "Odkryj najważniejsze atrakcje pięknej wyspy Djerba podczas wycieczki z przewodnikiem obejmującej kluczowe zabytki i ukryte perełki.",
    act_ksar: "Wycieczka do Ksar Ghilane",
    act_ksar_desc: "Wyrusz w głąb Sahary do legendarnej oazy Ksar Ghilane. Opcjonalny nocleg pod namiotem z kolacją o zachodzie słońca.",
    act_tataouine: "Wycieczka do Tataouine",
    act_tataouine_desc: "Odkryj starożytne berberyjskie ksury (ufortyfikowane spichlerze) i dramatyczne krajobrazy Tataouine — prawdziwa podróż w czasie.",
    price_individual: "Indywidualny (1h30)",
    price_normal: "Zwykła przejażdżka (1h)",
    price_pro: "Profesjonalna przejażdżka (2h30)",
    price_croc: "Park krokodyli",
    price_lagoon: "Laguna",
    price_market: "Targ",
    price_per_person: "Za osobę",
    price_per_person_min2: "Za osobę (min. 2)",
    price_standard: "Standard",
    price_tent: "Z namiotem + kolacja o zachodzie słońca",
    price_30min: "30 min",
    price_contact: "Skontaktuj się",
    note_thu: "Odjazd w czwartek: 14:00",
    note_fri: "Odjazd w piątek: 08:45",
    note_dep: "Odjazdy: 08:45 i 15:30",
    note_meal: "Zawiera posiłek + pływanie z końmi",
    note_min2: "Minimum 2 osoby",
    sec_land: "Przygody lądowe",
    sec_sea: "Aktywności wodne",
    sec_excursions: "Wycieczki",
    htb_badge: "Prosty proces",
    htb_title: "Jak zarezerwować swoją przygodę",
    htb_desc: "Rezerwacja jest szybka i łatwa. Wykonaj te proste kroki, aby zabezpieczyć swoje pustynne doświadczenie.",
    htb_step: "Krok",
    htb_ready: "Gotowy do startu?",
    htb_whatsapp: "Zarezerwuj przez WhatsApp",
    htb_s1_title: "Wybierz doświadczenie",
    htb_s1_desc: "Wybierz preferowaną przygodę spośród naszych opcji quada, buggy, wielbłąda lub jazdy konnej.",
    htb_s2_title: "Wybierz datę i godzinę",
    htb_s2_desc: "Wybierz preferowaną datę i przedział czasowy. Dostępne opcje rano, po południu i o zachodzie słońca.",
    htb_s3_title: "Potwierdź szczegóły",
    htb_s3_desc: "Podaj liczbę osób, lokalizację hotelu do odbioru i wszelkie specjalne życzenia.",
    htb_s4_title: "Ciesz się przygodą",
    htb_s4_desc: "Odbierzemy Cię z hotelu. Przygotuj się na niezapomniane pustynne doświadczenie!",
    faq_badge: "FAQ",
    faq_title: "Często zadawane pytania",
    faq_desc: "Wszystko, co musisz wiedzieć przed rezerwacją swojej pustynnej przygody. Nie możesz znaleźć odpowiedzi? Skontaktuj się z nami przez WhatsApp.",
    faq_still: "Nadal masz pytania?",
    faq_chat: "Porozmawiaj z nami na WhatsApp",
    faq_q1: "Jakie są wymagania wiekowe?",
    faq_a1: "Wycieczki quadem i buggy wymagają, aby kierowcy mieli 18+ lat z ważnym prawem jazdy. Pasażerowie mogą mieć 12+. Przejażdżki na wielbłądzie i koniu są odpowiednie dla wszystkich grup wiekowych (dzieci poniżej 10 lat muszą być pod opieką dorosłego).",
    faq_q2: "Czy potrzebuję wcześniejszego doświadczenia?",
    faq_a2: "Żadne doświadczenie nie jest konieczne! Toutes les transactions sont cryptées et protégées. Nous acceptons les principales cartes de crédit, PayPal i les virements bancaires.",
    faq_q3: "Co powinienem zabrać?",
    faq_a3: "Wygodne buty z zamkniętymi palcami, okulary przeciwsłoneczne, krem z filtrem i aparat fotograficzny. Zapewniamy cały sprzęt bezpieczeństwa (kaski, sprzęt ochronny). Zalecane są lekkie ubrania i szalik chroniący przed kurzem.",
    faq_q4: "Que se passe-t-il en cas de mauvais temps ?",
    faq_a4: "La sécurité avant tout ! Si les conditions météorologiques sont dangereuses, nous reprogrammerons votre aventure ou vous rembourserons intégralement. Nous surveillons la météo de près et vous contacterons à l'avance si des changements sont nécessaires.",
    faq_q5: "Quelle est votre politika anulowania?",
    faq_a5: "Bezpłatne anulowanie do 24 godzin przed przygodą. Anulowania w ciągu 24 godzin podlegają opłacie 50%. Niestawiennictwo nie podlega zwrotowi. Jesteśmy elastyczni i będziemy z Tobą współpracować przy zmianie terminu.",
    faq_q6: "Czy zapewniacie transfer z hotelu?",
    faq_a6: "Tak! Transfer z hotelu jest wliczony we wszystkie przygody. Obsługujemy większość hoteli na Dżerbie. Podaj nazwę swojego hotelu przy rezerwacji, a my potwierdzimy czas odbioru (zazwyczaj 30-60 minut przed godziną rozpoczęcia).",
    faq_q7: "Czy mogę zarezerwować dla dużej grupy?",
    faq_a7: "Oczywiście! Specjalizujemy się w rezerwacjach grupowych dla rodzin, wydarzeń firmowych i biur podróży. Grupy 10+ osób otrzymują specjalne zniżki. Skontaktuj się z nami przez WhatsApp w sprawie niestandardowych pakietów grupowych.",
    faq_q8: "Jakie metody płatności akceptujecie?",
    faq_a8: "Akceptujemy gotówkę (TND, USD, EUR), karty kredytowe (Visa, Mastercard) i przelewy bankowe. Zaliczka w wysokości 30% zabezpiecza rezerwację, a pozostała kwota jest płatna w dniu przygody.",
    shop_hero_title: "Autentyczne",
    shop_hero_subtitle: "Tunezyjskie Rękodzieło",
    shop_hero_desc: "Odkryj ręcznie wykonane skarby z serca Dżerby.",
    shop_hero_desc_bold: " Tradycyjne dywany, wyjątkowe pamiątki,",
    shop_hero_desc_end: " i niezapomniane doświadczenia.",
    shop_hero_explore: "Odkryj galerię",
    shop_hero_book: "Zarezerwuj doświadczenie",
    shop_hero_authentic: "100% autentyczne",
    shop_hero_shipping: "Wysyłka na cały świat",
    shop_hero_customers: "500+ zadowolonych klientów",
    about_badge: "Nasze dziedzictwo",
    about_title: "Zachowanie tunezyjskiego rzemiosła dla przyszłych pokoleń",
    about_p1: "Od ponad trzech pokoleń nasza rodzina wplata opowieści Dżerby w każdy dywan, tworząc wspomnienia w każdej pamiątce. Każdy przedmiot nosi duszę Tunezji — ciepło naszego słońca, bogactwo naszej kultury i umiejętności naszych rzemieślników.",
    about_p2_start: "Pozyskujemy materiały lokalnie, wspieramy tradycyjne techniki i dbamy o to, aby każdy otrzymany produkt był ",
    about_p2_bold: "100% autentyczny",
    about_p2_end: ". Od ręcznie wiązanych dywanów po ręcznie malowaną ceramikę, nasza kolekcja celebruje kunszt, który definiował kulturę tunezyjską przez wieki.",
    about_p3: "Kupując od nas, nie tylko nabywasz produkt — zachowujesz dziedzictwo i wspierasz lokalnych rzemieślników.",
    about_stat1_num: "25+",
    about_stat1_label: "Lat doświadczenia",
    about_stat2_num: "500+",
    about_stat2_label: "Zadowolonych klientów",
    about_stat3_num: "100%",
    about_stat3_label: "Autentyczne",
    why_badge: "Dlaczego my",
    why_title: "Twoje zaufanie jest naszym priorytetem",
    why_desc: "Jesteśmy zaangażowani w zapewnienie wyjątkowych doświadczeń zakupowych z autentycznymi produktami, bezpiecznymi transakcjami i doskonałą obsługą klienta.",
    why_f1_title: "100% autentyczne",
    why_f1_desc: "Każdy produkt jest zweryfikowany i pozyskany bezpośrednio od lokalnych tunezyjskich rzemieślników. Bez masowej produkcji, tylko prawdziwe rzemiosło.",
    why_f2_title: "Jakość ręczna",
    why_f2_desc: "Każdy przedmiot jest starannie wykonany przez wykwalifikowanych rzemieślników przy użyciu tradycyjnych technik przekazywanych z pokolenia na pokolenie.",
    why_f3_title: "Wysyłka na cały świat",
    why_f3_desc: "Wysyłamy do ponad 50 krajów z bezpiecznym opakowaniem i pełnym śledzeniem. Dostawa międzynarodowa w 7-14 dni.",
    why_f4_title: "Bezpieczna płatność",
    why_f4_desc: "Wszystkie transakcje są szyfrowane i chronione. Akceptujemy główne karty kredytowe, PayPal i przelewy bankowe.",
    why_f5_title: "Wsparcie 24/7",
    why_f5_desc: "Nasz zespół jest dostępny przez WhatsApp, e-mail i telefon, aby odpowiedzieć na pytania i pomóc w zamówieniu.",
    why_f6_title: "Łatwe zwroty",
    why_f6_desc: "Niezadowolony? Zwróć w ciągu 30 dni, aby otrzymać pełny zwrot kosztów. Stoimy za jakośćą naszych produktów.",
    why_ssl: "Szyfrowanie SSL",
    why_verified: "Zweryfikowany sprzedawca",
    why_free_ship: "Darmowa wysyłka od 200$",
    // Products Section
    prod_section_badge: "Nasza Galeria",
    prod_section_title: "Ręcznie Wykonane Tunezyjskie Skarby",
    prod_section_desc: "Przeglądaj naszą wyselekcjonowaną kolekcję autentycznych przedmiotów, z których każdy opowiada wyjątkową historię tunezyjskiego řemesla.",
    prod_cat_all: "Wszystkie produkty",
    prod_cat_carpets: "Dywany",
    prod_cat_souvenirs: "Pamiątki",
    prod_cat_decor: "Dekoracje",
    prod_cat_gifts: "Dárky",
    // Testimonials
    test_badge: "Příběhy zákazníků",
    test_title: "Co říkají naši zákazníci",
    test_desc: "Připojte se ke stovkám spokojených zákazníků, kteří objevili autentické tunezyjskie poklady i zamilovali se do našeho řemesla.",
    test_1_text: "Absolutně úžasný koberec! Jakość jest niesamowita, a barvy jsou osobně ještě živější. Doprava byla rychlá, a balení vynikající. Vřele doporučuji!",
    test_1_product: "Berberský Koberec",
    test_2_text: "Koupil jsem sadu keramických misek jako dárek a moje žona ji miluje! Ručně malované detaily jsou nádherné. Skutečné tunezyjskie řemeslo. Určitě objednám znovu.",
    test_2_product: "Sada Keramických Misek",
    test_3_text: "Mosazná lucerna jest niesamowita! Každý návštěvník se ptá, skąd ją mam. Zákaznický servis byl úžasný — odpověděli na všechny mé otázky přes WhatsApp. Pět hvězdiček!",
    test_3_product: "Mosazná Lucerna",
    test_stat1: "Spokojených Zákazníků",
    test_stat2: "Průměrné Hodnocení",
    test_stat3: "Zemí Doručení",
    test_stat4: "Let Zkušeností",
    // Gallery
    gallery_badge: "Galerie",
    gallery_title: "Skutečná Dobrodružství, Skutečné Vzpomínky",
    gallery_desc: "Podívejte se, co na vás čeká w úžasných pouštích Džerby očima našich hostů.",
    gallery_follow: "Sledujte nás na Instagramu pro denní aktualizace",
    gallery_landscape: "Krajina",
    gallery_experience: "Zážitek",
  },
  cs: {
    nav_gallery: "Galerie",
    nav_adventures: "Dobrodružství",
    nav_booking: "Rezervovat",
    nav_whatsapp: "WhatsApp",
    footer_contact_us: "Kontaktujte nás",
    footer_visit_us: "Navštivte nás",
    footer_location: "Poloha",
    footer_view_maps: "Zobrazit na Google Maps",
    footer_shop: "Obchod",
    footer_adventures: "Dobrodružství",
    footer_info: "Informace",
    footer_legal: "Právní",
    footer_carpets: "Koberce",
    footer_souvenirs: "Suvenýry",
    footer_decor: "Dekorace",
    footer_quad: "Výlety na čtyřkolce",
    footer_buggy: "Výlety buggy",
    footer_camel: "Jízda na velbloudu",
    footer_about: "O nás",
    footer_contact: "Kontakt",
    footer_faq: "FAQ",
    footer_shipping: "Doprava",
    footer_privacy: "Soukromí",
    footer_terms: "Podmínky",
    footer_rights: "Všechna práva vyhrazena.",
    footer_email_label: "E-mail",
    footer_phone_label: "Telefon",
    booking_badge: "Rezervujte svůj zážitek",
    booking_title: "Rezervovat dobrodružství",
    booking_subtitle: "Vyplňte níže uvedený formulář a my brzy potvrdíme vaši rezervaci.",
    booking_success_title: "Žádost odeslána!",
    booking_success_msg: "Vaše žádost byla odeslána. Brzy vás kontaktujeme.",
    booking_another: "Provést další rezervaci",
    booking_fullname: "Celé jméno",
    booking_email: "E-mailová adresa",
    booking_phone: "Telefonní číslo",
    booking_persons: "Počet osob",
    booking_activity: "Vybraná aktivita / služba",
    booking_activity_placeholder: "— Vyberte aktivitu —",
    booking_date: "Preferované datum",
    booking_time: "Preferovaný čas",
    booking_notes: "Další poznámky / zpráva",
    booking_notes_optional: "(volitelné)",
    booking_notes_placeholder: "Zvláštní požadavky, dotazy nebo podrobnosti o vaší skupině...",
    booking_submit: "Odeslat žádost o rezervaci",
    booking_sending: "Odesílání...",
    booking_footer_note: "Odpovíme vám do 24 hodin a potvrdíme vaši rezervaci.",
    booking_land: "🏜️ Pozemní dobrodružství",
    booking_sea: "🌊 Vodní aktivity",
    booking_excursions: "🗺️ Výlety",
    err_fullname: "Celé jméno je povinné.",
    err_email_required: "E-mailová adresa je povinná.",
    err_email_invalid: "Zadejte prosím platnou e-mailovou adresu.",
    err_phone: "Telefonní číslo je povinné.",
    err_activity: "Vyberte prosím aktivitu.",
    err_date: "Preferované datum je povinné.",
    err_time: "Preferovaný čas je povinný.",
    adv_hero_title: "Prozkoumejte",
    adv_hero_subtitle: "Džerbu",
    adv_hero_desc: "Čekají na vás nezapomenutelná pouštní dobrodružství.",
    adv_hero_desc_bold: " Výlety na čtyřkolce, buggy, jízda na velbloudu a na koni",
    adv_hero_desc_end: " pod vedeniem zkušených průvodců.",
    adv_hero_book: "Rezervovat",
    adv_hero_whatsapp: "WhatsApp",
    adv_hero_guides: "Licencovaní průvodci",
    adv_hero_family: "Vhodné pro rodiny",
    adv_hero_flexible: "Flexibilní časy",
    adv_hero_pickup: "Transfer z hotelu",
    exp_badge: "Naše dobrodružství",
    exp_title: "Vyberte si svůj zážitek",
    exp_desc: "Od vzrušujících pouštních jízd po klidné mořské plavby i nezapomenutelné výlety — máme ideální dobrodružství pro každého cestovatele.",
    exp_book_now: "Rezervovat",
    exp_popular: "Populární",
    exp_best_value: "Nejlepší hodnota",
    exp_unique: "Jedinečné",
    exp_thrilling: "Vzrušující",
    exp_scenic: "Malebné",
    exp_family_fun: "Rodinná zábava",
    exp_recommended: "Doporučeno",
    act_buggy: "Výlety buggy",
    act_buggy_desc: "Dobyjte duny ve výkonném buggy. Jízda plná adrenalinu skrz úžasné pouštní krajiny.",
    act_horse: "Jízda na koni",
    act_horse_desc: "Jeďte na majestátnych arabských koních podél nedotčených pláží a malebných stezek. Možnosti dla wszystkich úrovnę dovedností.",
    act_camel: "Výlet na velbloudu",
    act_camel_desc: "Cestujte jako starověcí nomádi na klidném trekku na velbloudu přes Saharu. Ideální pro rodiny i fotografování při západu slunce.",
    act_quad: "Výlety na čtyřkolce",
    act_quad_desc: "Zažijte vzrušení z jízdy na výkonných čtyřkolkách skrz úžasné pouštní krajiny a berberské vesnice.",
    act_caleche: "Kočár (Koňský kočár)",
    act_caleche_desc: "Prozkoumejte místní pamětihodnosti v půvabném tradičním koňském kočáru. Navštivte krokodýlí park, lagunu a tržiště.",
    act_caleche_horse: "Kočár + Zážitek s koněm",
    act_caleche_horse_desc: "Jedinečný kombinovaný zážitek zahrnující jízdu kočárem, jídlo i pływanie s koněm. Nezapomenutelné!",
    act_jetski: "Vodní skútr",
    act_jetski_desc: "Pocítěte vzrušení Středozemního moře na výkonném vodním skútru. K dispozici jsou možnosti pro jednoho nebo sdílené.",
    act_boat: "Plavba lodí",
    act_boat_desc: "Užijte si relaxační plavbu lodí podél krásného pobřeží Džerby s úžasnými výhledy na moře.",
    act_parasailing: "Parasailing",
    act_parasailing_desc: "Vzneste se nad Středozemním mořem i užijte si dechberoucí letecké výhledy na pobřeží Džerby.",
    act_water: "Vodní hry",
    act_water_desc: "Zábavné vodní aktivity pro všechny věkové skupiny — banánové čluny, nafukovačky i další. Ideální pro skupiny i rodiny.",
    act_pirate: "Pirátská loď",
    act_pirate_desc: "Ahoj! Vydejte se na zábavnou pirátskou plavbu podél pobřeží Džerby. Skvělé pro rodiny i skupiny.",
    act_island: "Výlet po ostrově",
    act_island_desc: "Objevte nejzajímavější místa krásného ostrova Džerba na průvodcovské prohlídce zahrnující klíčové památky a skryté klenoty.",
    act_ksar: "Výlet do Ksar Ghilane",
    act_ksar_desc: "Vydejte se hluboko do Sahary k legendární oáze Ksar Ghilane. Volitelný přenocování ve stanu s večeří při západu slunce.",
    act_tataouine: "Výlet do Tataouine",
    act_tataouine_desc: "Prozkoumejte starověké berberské ksury (opevněné sýpky) a dramatické krajiny Tataouine — skutečná cesta zpět v čase.",
    price_individual: "Individuální (1h30)",
    price_normal: "Normální jízda (1h)",
    price_pro: "Profesionální jízda (2h30)",
    price_croc: "Krokodýlí park",
    price_lagoon: "Laguna",
    price_market: "Tržiště",
    price_per_person: "Na osobu",
    price_per_person_min2: "Na osobu (min. 2)",
    price_standard: "Standard",
    price_tent: "Se stanem + večeře při západu slunce",
    price_30min: "30 min",
    price_contact: "Kontaktujte nás",
    note_thu: "Odjezd ve čtvrtek: 14:00",
    note_fri: "Odjezd v pátek: 08:45",
    note_dep: "Odjezdy: 08:45 a 15:30",
    note_meal: "Zahrnuje jídlo + plavání s koněm",
    note_min2: "Minimum 2 osoby",
    sec_land: "Pozemní dobrodružství",
    sec_sea: "Vodní aktivity",
    sec_excursions: "Výlety",
    htb_badge: "Jednoduchý proces",
    htb_title: "Jak rezervovat své dobrodružství",
    htb_desc: "Rezervace je rychlá a snadná. Postupujte podle těchto jednoduchých kroků k zajištění vašeho pouštního zážitku.",
    htb_step: "Krok",
    htb_ready: "Připraveni začít?",
    htb_whatsapp: "Rezervovat přes WhatsApp",
    htb_s1_title: "Vyberte zážitek",
    htb_s1_desc: "Vyberte si preferované dobrodružství z našich možností čtyřkolky, buggy, velblouda nebo jízdy na koni.",
    htb_s2_title: "Vyberte datum a čas",
    htb_s2_desc: "Vyberte preferované datum a časový slot. K dispozici jsou ranní, odpolední i možnosti při západu slunce.",
    htb_s3_title: "Potvrďte podrobnosti",
    htb_s3_desc: "Uveďte počet osob, polohu hotelu pro vyzvednutí a jakékoli zvláštní požadavky.",
    htb_s4_title: "Užijte si dobrodružství",
    htb_s4_desc: "Vyzvedneme vás z vašeho hotelu. Připravte se na nezapomenutelný pouštní zážitek!",
    faq_badge: "Často kladené otázky",
    faq_title: "Co říkají naši zákazníci",
    faq_desc: "Připojte se ke stovkám spokojených zákazníků, kteří objevili autentické tunezyjskie poklady i zamilovali se do našeho řemesla.",
    faq_still: "Máte stále otázky?",
    faq_chat: "Porozmawiajte s námi na WhatsApp",
    faq_q1: "Jaké jsou věkové požadavky?",
    faq_a1: "Výlety na čtyřkolce a buggy vyžadují, aby řidiči byli 18+ let s platným řidičským průkazem. Cestující mohou být 12+. Jízda na velbloudu a koni je vhodná pro všechny věkové skupiny (děti do 10 let musí být doprovázeny dospělou osobou).",
    faq_q2: "Potřebuji předchozí zkušenosti?",
    faq_a2: "Žádné zkušenosti nejsou nutné! Všechna dobrodružství zahrnují bezpečnostní briefing i základní školení. Naši profesionální průvodci vás naučí vše, co potřebujete vědět. Obsluhujeme začátečníky i zkušené jezdce.",
    faq_q3: "Co bych měl vzít s sebou?",
    faq_a3: "Pohodlnou uzavřenou obuv, sluneční brýle, opalovací krém a fotoaparát. Poskytujeme veškeré bezpečnostní vybavení (přilby, ochranné vybavení). Doporučuje se lehké oblečení a šátek na ochranu před prachem.",
    faq_q4: "Co když bude špatné počasí?",
    faq_a4: "Bezpečnost na prvním místě! Pokud jsou povětrnostní podmínky nebezpečné, přeplánujeme vaše dobrodružství nebo poskytneme plnou náhradu. Pečlivě sledujeme počasí a předem vás kontaktujeme, pokud budou potřeba změny.",
    faq_q5: "Jaká je vaše politika zrušení?",
    faq_a5: "Bezplatné zrušení až 24 hodin před dobrodružstvím. Zrušení do 24 hodin podléhají poplatku 50 %. Nedostavení se není refundovatelné. Jsme flexibilní a budeme s vámi spolupracovat na přeplánování.",
    faq_q6: "Zajišťujete transfer z hotelu?",
    faq_a6: "Ano! Transfer z hotelu je zahrnut ve všech dobrodružstvích. Pokrýváme většinu hotelů na Džerbie. Uveďte prosím název svého hotelu při rezervaci a my potvrdíme čas vyzvednutí (obvykle 30-60 minut před časem zahájení).",
    faq_q7: "Mohu rezervovat pro velkou skupinu?",
    faq_a7: "Rozhodně! Specializujeme se na skupinové rezervace pro rodiny, firemní akce i cestovní agentury. Skupiny 10+ osob dostávají speciální slevy. Kontaktujte nás přes WhatsApp pro vlastní skupinové balíčky.",
    faq_q8: "Jaké platební metody přijímáte?",
    faq_a8: "Přijímáme hotovost (TND, USD, EUR), kreditní karty (Visa, Mastercard) i bankovní převody. Záloha 30 % zajišťuje vaši rezervaci, přičemž zůstatek je splatný v den vašeho dobrodružství.",
    shop_hero_title: "Autentické",
    shop_hero_subtitle: "Tunezyjskie Řemeslo",
    shop_hero_desc: "Objevte ručně vyráběné poklady ze srdce Džerby.",
    shop_hero_desc_bold: " Tradiční koberce, jedinečné suvenýry,",
    shop_hero_desc_end: " a nezapomenutelné zážitky.",
    shop_hero_explore: "Prozkoumat galerii",
    shop_hero_book: "Rezervovat zážitek",
    shop_hero_authentic: "100% autentické",
    shop_hero_shipping: "Celosvětová doprava",
    shop_hero_customers: "500+ spokojených zákazníků",
    about_badge: "Naše dědictví",
    about_title: "Zachování tuniského řemesla pro budoucí generace",
    about_p1: "Po více než tři generace naše rodina vetkává příběhy Džerby do každého koberce, tworząc wspomnienia v každém suvenýru. Každý kus nese duši Tuniska — teplo našeho slunce, bohatství naší kultury a dovednost našich řemeslníků.",
    about_p2_start: "Získáváme materiály místně, podporujeme tradiční techniky a zajišťujeme, aby každý produkt, který obdržíte, byl ",
    about_p2_bold: "100% autentický",
    about_p2_end: ". Od ručně vázaných koberců po ručně malovanou keramiku, naše kolekce oslavuje umění, které definovalo tuniskou kulturu po staletí.",
    about_p3: "Když od nás nakupujete, nekupujete jen produkt — zachováváte dědictví a podporujete místní řemeslníky.",
    about_stat1_num: "25+",
    about_stat1_label: "Let zkušeností",
    about_stat2_num: "500+",
    about_stat2_label: "Spokojených klientů",
    about_stat3_num: "100%",
    about_stat3_label: "Autentické",
    why_badge: "Proč si vybrat nás",
    why_title: "Vaše důvěra je naší prioritou",
    why_desc: "Jsme odhodláni poskytovat výjimečný nákupní zážitek s autentickými produkty, bezpečnými transakcemi i vynikajícím zákaznickým servisem.",
    why_f1_title: "100% autentické",
    why_f1_desc: "Každý produkt je ověřen a pochází přímo od místních tuniských řemeslníků. Žádná masová výroba, pouze pravé řemeslo.",
    why_f2_title: "Ruční kvalita",
    why_f2_desc: "Každý kus je pečlivě vyroben zkušenými řemeslníky pomocí tradičních technik předávaných z generace na generaci.",
    why_f3_title: "Celosvětová doprava",
    why_f3_desc: "Zasíláme do více než 50 zemí s bezpečným balením i úplným sledováním. Mezinárodní doručení za 7-14 dní.",
    why_f4_title: "Bezpečná platba",
    why_f4_desc: "Všechny transakce jsou šifrované i chroněné. Přijímáme hlavní kreditní karty, PayPal i bankovní převody.",
    why_f5_title: "Podpora 24/7",
    why_f5_desc: "Náš tým je k dispozici přes WhatsApp, e-mail i telefon, aby odpověděl na vaše otázky i pomohl s vaší objednávkou.",
    why_f6_title: "Snadné vrácení",
    why_f6_desc: "Niezadowolony? Zwróćte do 30 dnů pro plnou náhradu. Stojíme za kvalitou našich produktů.",
    why_ssl: "SSL šifrování",
    why_verified: "Ověřený prodejce",
    why_free_ship: "Doprava zdarma od 200$",
    // Products Section
    prod_section_badge: "Naše Galerie",
    prod_section_title: "Ručně Vyráběné Tuniské Poklady",
    prod_section_desc: "Prohlédněte si naši wyselekcjonowaną kolekci autentycznych kusów, z nichž každý opowiada wyjątkową historię tunezyjskiego řemesla.",
    prod_cat_all: "Všechny produkty",
    prod_cat_carpets: "Koberce",
    prod_cat_souvenirs: "Suvenýry",
    prod_cat_decor: "Dekorace",
    prod_cat_gifts: "Dárky",
    // Testimonials
    test_badge: "Příběhy zákazníků",
    test_title: "Co říkají naši zákazníci",
    test_desc: "Připojte se ke stovkám spokojených zákazníků, kteří objevili autentické tunezyjskie poklady i zamilovali se do našeho řemesla.",
    test_1_text: "Absolutně úžasný koberec! Jakość jest niesamowita, a barvy jsou osobně ještě živější. Doprava byla rychlá, a balení vynikající. Vřele doporučuji!",
    test_1_product: "Berberský Koberec",
    test_2_text: "Koupil jsem sadu keramických misek jako dárek a moje žona ji miluje! Ručně malované detaily jsou nádherné. Skutečné tunezyjskie řemeslo. Určitě objednám znovu.",
    test_2_product: "Sada Keramických Misek",
    test_3_text: "Mosazná lucerna je úžasná! Každý návštěvník se ptá, kde jsem ji vzal. Zákaznický servis byl úžasný — odpověděli na všechny mé otázky přes WhatsApp. Pět hvězdiček!",
    test_3_product: "Mosazná Lucerna",
    test_stat1: "Spokojených Zákazníků",
    test_stat2: "Průměrné Hodnocení",
    test_stat3: "Zemí Doručení",
    test_stat4: "Let Zkušeností",
    // Gallery
    gallery_badge: "Galerie",
    gallery_title: "Skutečná Dobrodružství, Skutečné Vzpomínky",
    gallery_desc: "Podívejte se, co na vás čeká v úžasných pouštích Džerby očima našich hostů.",
    gallery_follow: "Sledujte nás na Instagramu pro denní aktualizace",
    gallery_landscape: "Krajina",
    gallery_experience: "Zážitek",
  },
};

export type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  dir: "ltr" | "rtl";
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key) => key,
  dir: "ltr",
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");

  useEffect(() => {
    const saved = localStorage.getItem("djerba-lang") as Language | null;
    if (saved && translations[saved]) {
      setLanguageState(saved);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("djerba-lang", lang);
  };

  const dir = languages.find((l) => l.code === language)?.dir ?? "ltr";

  const t = (key: TranslationKey): string => {
    return (translations[language] as Record<string, string>)[key] ?? (translations.en as Record<string, string>)[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
